// Planning réellement appliqué à une date : l'exception de cette date si elle existe,
// sinon le planning hebdomadaire du jour de la semaine.
// C'est exactement la règle du backend (creneauController, reservationController) :
// le front doit s'y tenir, sinon il refuse un jour que le backend accepte (ou l'inverse).

// 'YYYY-MM-DD' en heure locale (jamais toISOString, qui décale d'un jour selon le fuseau).
export const formatDateISO = (date) =>
  date.getFullYear() + '-' +
  String(date.getMonth() + 1).padStart(2, '0') + '-' +
  String(date.getDate()).padStart(2, '0');

// Le backend numérote les jours de 1 (lundi) à 7 (dimanche), alors que
// Date.getDay() renvoie 0 pour dimanche.
export const jourSemaineBackend = (date) => date.getDay() || 7;

// Indexe une liste d'exceptions (réponse de l'API) par date 'YYYY-MM-DD'.
export const indexerExceptions = (exceptions) => {
  const map = {};
  // Réponse inattendue (pas un tableau) : aucune exception plutôt qu'une erreur,
  // pour retomber sur le planning hebdomadaire au lieu de bloquer la page.
  if (!Array.isArray(exceptions)) return map;
  for (const e of exceptions) {
    if (e && e.date) map[String(e.date).slice(0, 10)] = e;
  }
  return map;
};

// Renvoie { actif, mode, plages, departements, exception: bool } ou null si aucun planning.
export const planningPourDate = (date, planningHebdo, exceptionsParDate) => {
  if (!date) return null;
  const exc = exceptionsParDate ? exceptionsParDate[formatDateISO(date)] : null;
  if (exc) {
    return {
      actif: !!exc.actif,
      mode: exc.actif ? exc.mode : null,
      plages: exc.plages || [],
      departements: exc.departements || [],
      exception: true,
    };
  }
  const js = jourSemaineBackend(date);
  const hebdo = (planningHebdo || []).find(p => Number(p.jour_semaine) === js);
  if (!hebdo) return null;
  return {
    actif: !!hebdo.actif,
    mode: hebdo.actif ? hebdo.mode : null,
    plages: hebdo.plages || [],
    departements: hebdo.departements || [],
    exception: false,
  };
};

// ── Messages aux clientes pendant une semaine exceptionnelle ──────────────────────────
// Seuls les jours portant une exception sont concernés : une semaine normale ne produit
// aucun message, la page se comporte alors exactement comme avant.

const NOMS_JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const NOMS_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const lendemain = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
const proposeMode = (planning, mode) => !!planning && planning.actif && planning.mode === mode;

// Jours de [debut, fin] où une exception change ce que la cliente peut réserver dans `mode` :
// - retraits : le planning normal proposait ce mode, l'exception ne le propose plus
//   (jour fermé, ou passé dans l'autre mode) ;
// - ajouts : le planning normal ne le proposait pas, l'exception le propose.
// Une exception qui garde le mode (autres horaires, autres villes) n'est ni l'un ni l'autre.
export const changementsExceptionnels = (debut, fin, mode, planningHebdo, exceptionsParDate) => {
  const retraits = [];
  const ajouts = [];
  if (!debut || !fin || !mode || !exceptionsParDate) return { retraits, ajouts };
  for (let d = new Date(debut.getFullYear(), debut.getMonth(), debut.getDate()); d <= fin; d = lendemain(d)) {
    if (!exceptionsParDate[formatDateISO(d)]) continue;
    const avant = proposeMode(planningPourDate(d, planningHebdo, null), mode);
    const apres = proposeMode(planningPourDate(d, planningHebdo, exceptionsParDate), mode);
    if (avant && !apres) retraits.push(d);
    else if (!avant && apres) ajouts.push(d);
  }
  return { retraits, ajouts };
};

// Premier jour APRÈS `apres` où le planning appliqué (exception > hebdo) propose `mode`.
// Basé sur le planning, pas sur les places restantes : c'est « la reprise », pas un créneau libre.
// `limite` : dernier jour pour lequel les exceptions sont connues ; au-delà on ne sait pas si
// un jour est réellement ouvert, donc on ne propose aucune date plutôt qu'une date fausse.
export const prochaineDateMode = (apres, mode, planningHebdo, exceptionsParDate, maxJours = 90, limite = null) => {
  if (!apres || !mode) return null;
  for (let i = 1; i <= maxJours; i++) {
    const d = new Date(apres.getFullYear(), apres.getMonth(), apres.getDate() + i);
    if (limite && d > limite) return null;
    if (proposeMode(planningPourDate(d, planningHebdo, exceptionsParDate), mode)) return d;
  }
  return null;
};

const memeJour = (a, b) => formatDateISO(a) === formatDateISO(b);

// Périodes de jours qui se suivent : { debut, fin, jours }.
export const periodesConsecutives = (dates) => {
  const periodes = [];
  for (const d of [...(dates || [])].sort((a, b) => a - b)) {
    const p = periodes[periodes.length - 1];
    if (p && memeJour(lendemain(p.fin), d)) {
      p.fin = d;
      p.jours.push(d);
    } else {
      periodes.push({ debut: d, fin: d, jours: [d] });
    }
  }
  return periodes;
};

// Périodes de jours RETIRÉS d'un mode. Deux jours retirés restent dans la même période si
// aucun jour entre eux n'est réservable dans ce mode : les jours « creux » (dimanche, jours
// de l'autre mode) ne changent rien pour la cliente. Ainsi un mois de fermeture donne
// « du mercredi 4 au samedi 28 novembre » et non quatre semaines listées une à une.
// (Pas pour les ajouts : « je me déplace du mercredi 18 au vendredi 20 » laisserait croire
// que le jeudi est inclus.)
export const periodesRetraits = (dates, mode, planningHebdo, exceptionsParDate) => {
  const periodes = [];
  for (const d of [...(dates || [])].sort((a, b) => a - b)) {
    const p = periodes[periodes.length - 1];
    let continu = false;
    if (p) {
      continu = true;
      for (let x = lendemain(p.fin); x < d && !memeJour(x, d); x = lendemain(x)) {
        if (proposeMode(planningPourDate(x, planningHebdo, exceptionsParDate), mode)) {
          continu = false;
          break;
        }
      }
    }
    if (continu) {
      p.fin = d;
      p.jours.push(d);
    } else {
      periodes.push({ debut: d, fin: d, jours: [d] });
    }
  }
  return periodes;
};

const numeroJour = (d) => (d.getDate() === 1 ? '1er' : String(d.getDate()));

// « lundi 23 novembre »
export const libelleDate = (d) => `${NOMS_JOURS[d.getDay()]} ${numeroJour(d)} ${NOMS_MOIS[d.getMonth()]}`;

// Formule des périodes : « le lundi 16 novembre », « le lundi 16 et le mardi 17 novembre »,
// « du jeudi 19 au samedi 21 novembre », plusieurs périodes reliées par « , » / « ainsi que ».
export const formulerPeriodes = (periodes) => {
  const jourSeul = (d) => `${NOMS_JOURS[d.getDay()]} ${numeroJour(d)}`;
  const phrases = (periodes || []).map(({ debut, fin }) => {
    if (memeJour(debut, fin)) return `le ${libelleDate(debut)}`;
    const premier = debut.getMonth() === fin.getMonth() ? jourSeul(debut) : libelleDate(debut);
    if (memeJour(lendemain(debut), fin)) return `le ${premier} et le ${libelleDate(fin)}`;
    return `du ${premier} au ${libelleDate(fin)}`;
  });
  if (phrases.length <= 1) return phrases[0] || '';
  // « ainsi que » plutôt que « et » : une période de deux jours contient déjà un « et »
  return `${phrases.slice(0, -1).join(', ')}, ainsi que ${phrases[phrases.length - 1]}`;
};

// Raccourci : jours regroupés quand ils se suivent.
export const formulerJours = (dates) => formulerPeriodes(periodesConsecutives(dates));
